import { useEffect, useState } from 'react';

import { Box, Dialog, DialogContent, SwipeableDrawer, useMediaQuery, useTheme } from '@mui/material';

import { useAppContext } from '@/context';
import { Maybe, Scalars } from '@/types/generated';

import { CloseButton } from '../TAGDialog/TAGDialog';
import { DynamicFormConfig, DynamicFormSchema as TDynamicFormSchema } from '../DynamicForm/types/DynamicFormTypes';
import ZenotiWrapper from '../DynamicForm/ZenotiWrapper';

import { getDialogWrapperStyles, getDrawerWrapperStyles, quizInPageWrapperStyles } from './Quiz.styles';
import { IStateObject, quizGA4DataLayerPush, TFormData, determineQuizEndpointOverwrite } from './Quiz.helpers';
import Questions from './Questions/Questions';
import UserDetailsForm from './UserDetailsForm/UserDetailsForm';

export interface IQuiz {
	/**
	 * Quiz Type for different business logic
	 */
	quizType: string;
	/**
	 * JSON Form data from contentful
	 */
	quizFormData: Maybe<Scalars['JSON']>;
	/**
	 * Quiz Submit endpoint url from contentful
	 */
	submitEndpointUrl: string;
	/**
	 * If <b>true</b>, the component is shown.
	 * @type {boolean}
	 *
	 */
	showInDialogOrDrawer?: boolean;
	/**
	 * If <b>true</b>, the component is shown.
	 * @type {boolean}
	 *
	 */
	openQuiz?: boolean;
	/**
	 * Callback to fire when the component needs to be closed.
	 */
	handleCloseQuiz?: () => void;
	/**
	 * If true, the component utilizes the FormBuilder,
	 * and completely bypasses all of Quiz
	 * @type {boolean}
	 */
	isFormBuilder?: boolean;
	/**
	 * JSON Form builder config from contentful
	 */
	formBuilderConfig?: DynamicFormConfig;
	/**
	 * redirect path for poor credit users for CC specific
	 */
	clearChoicePoorCreditRedirectPath?: string;
}

interface QuizFromJsonContentWrapper {
	showInDialogOrDrawer: boolean;
	handleClose: () => void;
	formType: string;
	state: IStateObject;
	updateState: (newState: Partial<IStateObject>) => void;
	submitEndpointUrl: string;
	quizType: string;
	clearChoicePoorCreditRedirectPath: string;
}
interface QuizFromFormBuilderContentWrapper {
	showInDialogOrDrawer: boolean;
	handleClose: () => void;
	quizFormData: TFormData;
	formBuilderConfig: DynamicFormConfig;
}

const QuizFromJsonContentWrapper = (props: QuizFromJsonContentWrapper) => {
	const {
		showInDialogOrDrawer = false,
		handleClose,
		formType,
		state,
		updateState,
		submitEndpointUrl,
		quizType,
		clearChoicePoorCreditRedirectPath,
	} = props;

	return (
		<>
			{showInDialogOrDrawer && <CloseButton onClose={handleClose} />}
			{/* Render Questions initially  */}
			{formType === 'Questions' && (
				<Questions state={state} setState={updateState} showInDialogOrDrawer={showInDialogOrDrawer} />
			)}
			{/* Render user details form */}
			{formType === 'UserDetails' && (
				<UserDetailsForm
					state={state}
					setState={updateState}
					submitEndpointUrl={submitEndpointUrl}
					showInDialogOrDrawer={showInDialogOrDrawer}
					quizType={quizType}
					clearChoicePoorCreditRedirectPath={clearChoicePoorCreditRedirectPath}
				/>
			)}
		</>
	);
};

const QuizFromFormBuilderContentWrapper = (props: QuizFromFormBuilderContentWrapper) => {
	const { showInDialogOrDrawer = false, handleClose, quizFormData, formBuilderConfig } = props;

	return (
		<>
			{showInDialogOrDrawer && <CloseButton onClose={handleClose} />}
			<ZenotiWrapper
				defaultFormData={{
					minAge: 18,
					minBMI: 27,
					todaysDate: new Date().toLocaleDateString('en-CA'), // to format as yyyy-mm-dd
					contactConsent: true,
				}}
				defaultSchema={quizFormData as unknown as TDynamicFormSchema}
				schemaJSON={JSON.stringify(quizFormData)}
				config={formBuilderConfig}
			/>
		</>
	);
};

export default function Quiz({
	quizType,
	quizFormData,
	submitEndpointUrl,
	openQuiz,
	handleCloseQuiz,
	showInDialogOrDrawer = false,
	isFormBuilder = false,
	formBuilderConfig = {},
	clearChoicePoorCreditRedirectPath = '',
}: Readonly<IQuiz>) {
	const staticForm: 'Questions' | 'UserDetails' =
		(quizFormData as TFormData)?.form?.form_questions?.length > 0 ? 'Questions' : 'UserDetails';
	const [state, setState] = useState<IStateObject>({
		activeQuestionIndex: 0,
		formData: null,
		formType: staticForm,
		fieldHasError: false,
	});

	const theme = useTheme();
	const { config, stateCodeFromHeaders } = useAppContext();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
	const brandName = config.name;

	const quizSubmissionEndpoint = determineQuizEndpointOverwrite({
		defaultSubmitEndpointUrl: submitEndpointUrl,
		brandName,
		enableHydra: config.featureFlags?.enableHydra || false,
		hydraPilotLocations: config.featureFlags?.hydraPilotLocations || [],
	});

	const { formType, formData } = state;

	useEffect(() => {
		if (brandName) {
			updateState({
				formData: quizFormData as TFormData,
				formType: isFormBuilder ? 'DynamicForm' : staticForm,
				activeQuestionIndex: 0,
			});
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [brandName]);

	const updateState = (newState: Partial<IStateObject>) => {
		setState({
			...state,
			...newState,
		});
	};

	const handleClose = () => {
		updateState({
			formData: quizFormData as TFormData,
			formType: isFormBuilder ? 'DynamicForm' : staticForm,
			activeQuestionIndex: 0,
		});

		if (!isFormBuilder) {
			quizGA4DataLayerPush(config.name, 'cancel', 'N/A', formData as TFormData, stateCodeFromHeaders);
		}
		if (handleCloseQuiz) {
			handleCloseQuiz();
		}
	};

	if ((showInDialogOrDrawer && !openQuiz) || !formData) {
		return null;
	}

	if (isFormBuilder) {
		// form builder quiz in a dialog
		if (showInDialogOrDrawer) {
			return (
				<>
					{isSmallScreen ? (
						<SwipeableDrawer
							open
							onClose={() => null}
							onOpen={() => null}
							anchor="bottom"
							sx={getDrawerWrapperStyles(true)}
							data-test-id={`${formType}_drawer`}
						>
							<QuizFromFormBuilderContentWrapper
								showInDialogOrDrawer
								handleClose={handleClose}
								quizFormData={quizFormData as TFormData}
								formBuilderConfig={formBuilderConfig}
							/>
						</SwipeableDrawer>
					) : (
						<Dialog
							open
							sx={getDialogWrapperStyles}
							fullWidth
							maxWidth="lg"
							data-test-id={`${formType}_dialog`}
						>
							<DialogContent>
								<QuizFromFormBuilderContentWrapper
									showInDialogOrDrawer
									handleClose={handleClose}
									quizFormData={quizFormData as TFormData}
									formBuilderConfig={formBuilderConfig}
								/>
							</DialogContent>
						</Dialog>
					)}
				</>
			);
		}

		// form builder quiz in a page
		return (
			<Box data-test-id="quiz_in_page">
				<QuizFromFormBuilderContentWrapper
					showInDialogOrDrawer={false}
					handleClose={() => null}
					quizFormData={quizFormData as TFormData}
					formBuilderConfig={formBuilderConfig}
				/>
			</Box>
		);
	}

	if (showInDialogOrDrawer) {
		// quiz from json in a dialog
		return (
			<>
				{isSmallScreen ? (
					<SwipeableDrawer
						open
						onClose={() => null}
						onOpen={() => null}
						anchor="bottom"
						sx={getDrawerWrapperStyles(false)}
						data-test-id={`${formType}_drawer`}
					>
						<QuizFromJsonContentWrapper
							showInDialogOrDrawer
							handleClose={handleClose}
							formType={formType}
							state={state}
							updateState={updateState}
							submitEndpointUrl={quizSubmissionEndpoint}
							quizType={quizType}
							clearChoicePoorCreditRedirectPath={clearChoicePoorCreditRedirectPath}
						/>
					</SwipeableDrawer>
				) : (
					<Dialog
						open
						sx={getDialogWrapperStyles}
						fullWidth
						maxWidth="lg"
						data-test-id={`${formType}_dialog`}
					>
						<QuizFromJsonContentWrapper
							showInDialogOrDrawer
							handleClose={handleClose}
							formType={formType}
							state={state}
							updateState={updateState}
							submitEndpointUrl={quizSubmissionEndpoint}
							quizType={quizType}
							clearChoicePoorCreditRedirectPath={clearChoicePoorCreditRedirectPath}
						/>
					</Dialog>
				)}
			</>
		);
	}

	// quiz from json in a page
	return (
		<Box
			data-test-id="quiz_in_page"
			sx={{
				...quizInPageWrapperStyles,
				...((quizFormData as TFormData)?.form?.formHeightAuto ? { minHeight: 'auto' } : {}),
			}}
		>
			<QuizFromJsonContentWrapper
				showInDialogOrDrawer={false}
				handleClose={handleClose}
				formType={formType}
				state={state}
				updateState={updateState}
				submitEndpointUrl={quizSubmissionEndpoint}
				quizType={quizType}
				clearChoicePoorCreditRedirectPath={clearChoicePoorCreditRedirectPath}
			/>
		</Box>
	);
}
